import { createContext, useContext } from 'react';

import type { ProductionState } from '@/hooks/useProduction';

/**
 * Everything the asset galleries need about the run they're rendering.
 *
 * Previously the console threaded these same eight props into DesignReferences,
 * KeyframeGallery, ClipsGallery and SourceFootage individually; any new gallery had
 * to repeat the whole list. They read from here instead.
 */
export interface ProductionContextValue {
  productionId?: string;
  assets: ProductionState['assets'];
  shotPlan: unknown;
  /** Shots with a rework/reshoot still queued — their tiles get the "queued" marker. */
  pendingRework: Set<string>;
  /** QC-accepted assets, keyed `type:NORMALIZED_ID`. Anything absent is still iterating. */
  finalAssets: Set<string>;
  running: boolean;
  /** Per-asset regeneration counters, used to cache-bust a reworked image. */
  reworkBust: Record<string, number>;
  /** 9:16 productions get portrait tiles — cropping would hide the composition. */
  vertical: boolean;
  /** Storyboard images are 8-panel sheets sent as a reference, not the clip's first frame. */
  storyboardSheet: boolean;
}

const Ctx = createContext<ProductionContextValue | null>(null);

export const ProductionProvider = Ctx.Provider;

export function useProductionContext(): ProductionContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useProductionContext() must be used inside <ProductionProvider>');
  return ctx;
}

/** Tailwind aspect class for a tile, honouring the production's aspect ratio. */
export function useTileAspect(): string {
  return useProductionContext().vertical ? 'aspect-[9/16]' : 'aspect-video';
}
